// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { fetchPatientEncounterNotes, type PatientEncounterNote } from '../utils/patient-export';

export interface UsePatientEncounterNotesResult {
  notes: PatientEncounterNote[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

export function usePatientEncounterNotes(patient: Patient | undefined): UsePatientEncounterNotesResult {
  const medplum = useMedplum();
  const [notes, setNotes] = useState<PatientEncounterNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setNotes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const nextNotes = await fetchPatientEncounterNotes(medplum, patient.id);
      setNotes(nextNotes);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load encounter notes'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { notes, loading, error, refresh: fetch };
}
